import { useContext, useState } from 'react';
import { LoginModel, UserProviderValue } from '../models';
import { UserContext } from '../contexts/user.context';
import { useForm } from 'react-hook-form';
import { LoginSchema } from '../schemas';
import { zodResolver } from '@hookform/resolvers/zod';
import { login, loginWithApple, loginWithGoogle } from '../services';
import { toast } from 'react-toastify';
import { useGoogleAuth } from './useGoogleAuth';
import { useAppleAuth } from './useAppleAuth';

export function useLoginWeb2() {
   const loginForm = useForm<LoginModel>({
      resolver: zodResolver(LoginSchema),
      mode: 'onChange',
   });
   const { setUser, setIsAuthenticated, setIsLoginModalOpen, fetchUser } = useContext<UserProviderValue>(UserContext);

   const [isLoading, setIsLoading] = useState(false);

   const submitLogin = async (data: LoginModel) => {
      setIsLoading(true);
      const loginResponse = await login(data);

      if (loginResponse.error || !loginResponse.data) {
         toast.error(loginResponse.error?.message || 'Failed to login');
         setIsLoading(false);
         return;
      }

      setUser(loginResponse.data);
      setIsAuthenticated(true);
      await fetchUser();
      setIsLoading(false);
      setIsLoginModalOpen(false);
   };

   const handleGoogleSuccess = async (idToken: string) => {
      const response = await loginWithGoogle({ idToken });

      if (response.error || !response.data) {
         toast.error(response.error?.message || 'Failed to sign in with Google');
         return;
      }

      setUser(response.data);
      setIsAuthenticated(true);
      await fetchUser();
      setIsLoginModalOpen(false);
   };

   const { signInWithGoogle, isGoogleReady } = useGoogleAuth({
      onSuccess: handleGoogleSuccess,
   });

   const handleAppleSuccess = async (idToken: string) => {
      const response = await loginWithApple({ idToken });

      if (response.error || !response.data) {
         toast.error(response.error?.message || 'Failed to sign in with Apple');
         return;
      }

      setUser(response.data);
      setIsAuthenticated(true);
      await fetchUser();
      setIsLoginModalOpen(false);
   };

   const { signInWithApple, isAppleReady } = useAppleAuth({
      onSuccess: handleAppleSuccess,
   });

   return {
      submitLogin,
      isLoading,
      loginForm,
      setIsLoginModalOpen,
      signInWithGoogle,
      isGoogleReady,
      signInWithApple,
      isAppleReady,
   };
}
